import { styled } from "styled-components";
import { Link } from "react-router-dom";
import { Flex } from "../components/Flex";
import { Input } from "../components/Input";
import Logo from "../assets/logo.svg";
import { useUI, MenuItems } from "../contexts";
import fb from "../assets/fb.svg";
import ig from "../assets/ig.svg";
import { StyledButton } from "../components/Typography";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { useCallback, useState } from "react";
import useNewsletter from "../hooks/useNewsletter";

const FooterWrapper = styled.footer`
  background: ${({ theme }) => theme.gradients.darkBlue};
  width: 100%;
  box-sizing: border-box;
  padding: 100px clamp(20px, 4vw, 60px) 40px;
  color: ${({ theme }) => theme.colors.white};
  overflow-x: hidden;

  @media (max-width: 767px) {
    padding: 60px 20px 40px;
  }
`;

const FooterContent = styled.div`
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 64px;
`;

const TopSection = styled.div`
  width: 100%;
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const BrandColumn = styled.div`
  min-width: 230px;
  flex: 1 1 260px;

  @media (max-width: 767px) {
    min-width: 100%;
  }
`;

const FooterColumn = styled.div`
  min-width: 180px;
  flex: 1 1 180px;

  @media (max-width: 767px) {
    min-width: 140px;
  }

  @media (max-width: 420px) {
    min-width: 100%;
  }
`;

const NewsletterRow = styled.div`
  width: 100%;
  max-width: 360px;
  display: flex;
  gap: 12px;
`;

const NewsletterInput = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const strankyLinks = [
  { name: "O nás", link: `/${MenuItems.AboutUs}`, menuItem: MenuItems.AboutUs },
  { name: "Aktuální aukce", link: `/${MenuItems.CurrentAuction}`, menuItem: MenuItems.CurrentAuction },
  { name: "Aukce", link: `/${MenuItems.Auctions}`, menuItem: MenuItems.Auctions },
  { name: "Výherci", link: `/${MenuItems.Winners}`, menuItem: MenuItems.Winners },
];

const podporaLinks = [
  { name: "Nápověda", link: `/${MenuItems.Help}`, menuItem: MenuItems.Help },
  { name: "Kontakt", link: `/${MenuItems.Contact}`, menuItem: MenuItems.Contact },
  { name: "Blog", link: `/${MenuItems.Blog}`, menuItem: MenuItems.Blog },
];

const menuLinks = [
  { name: "Domů", link: "/", menuItem: MenuItems.Home },
  { name: "Přihlášení", link: `/${MenuItems.LogIn}`, menuItem: MenuItems.LogIn },
];

const Footer = () => {
  const { setSelectedMenu } = useUI();
  const [email, setEmail] = useState("");
  const { addNewsletter } = useNewsletter();

  const onSendEmail = useCallback(() => {
    addNewsletter(email);
    setEmail("");
  }, [email, addNewsletter]);

  return (
    <FooterWrapper>
      <FooterContent>
        {/* Top Section */}
        <TopSection>
          <BrandColumn>
            <Flex vertical gap={40} style={{ width: "100%" }}>
              <Link to="/" onClick={() => setSelectedMenu(MenuItems.Home)}>
                <img src={Logo} alt="Logo" style={{ height: 48 }} />
              </Link>
              <Flex align="center" gap={34}>
                <Link to="https://www.facebook.com/trefim.to/" target="_blank">
                  <img src={fb} alt="Facebook" style={{ height: 18 }} />
                </Link>
                <Link to="https://www.instagram.com/trefim_to/" target="_blank">
                  <img src={ig} alt="Instagram" style={{ height: 18 }} />
                </Link>
              </Flex>
              <NewsletterRow>
                <NewsletterInput>
                  <Input
                    style={{ height: 44, borderRadius: 4 }}
                    placeholder="E-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </NewsletterInput>
                <StyledButton
                  style={{
                    minWidth: 44,
                    maxWidth: 44,
                    height: 44,
                    padding: 10,
                    borderRadius: 4,
                  }}
                  onClick={onSendEmail}
                >
                  <ArrowRightIcon style={{ width: 20, height: 20 }} />
                </StyledButton>
              </NewsletterRow>
            </Flex>
          </BrandColumn>
          <FooterColumn>
            <Flex vertical style={{ width: "100%" }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 400,
                  marginBottom: 15,
                  fontFamily: "Sofia Pro, sans-serif",
                }}
              >
                STRÁNKY
              </div>
              {strankyLinks.map((page, index) => (
                <Link
                  key={index}
                  to={page.link}
                  onClick={() => setSelectedMenu(page.menuItem)}
                  style={{
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: "37px",
                    color: "rgba(255, 255, 255, 0.6)",
                    fontFamily: "Sofia Pro, sans-serif",
                  }}
                >
                  {page.name}
                </Link>
              ))}
            </Flex>
          </FooterColumn>
          <FooterColumn>
            <Flex vertical style={{ width: "100%" }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 400,
                  marginBottom: 15,
                  fontFamily: "Sofia Pro, sans-serif",
                }}
              >
                PODPORA
              </div>
              {podporaLinks.map((page, index) => (
                <Link
                  key={index}
                  to={page.link}
                  onClick={() => setSelectedMenu(page.menuItem)}
                  style={{
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: "37px",
                    color: "rgba(255, 255, 255, 0.6)",
                    fontFamily: "Sofia Pro, sans-serif",
                  }}
                >
                  {page.name}
                </Link>
              ))}
            </Flex>
          </FooterColumn>
          <FooterColumn>
            <Flex vertical style={{ width: "100%" }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 400,
                  marginBottom: 15,
                  fontFamily: "Sofia Pro, sans-serif",
                }}
              >
                MENU
              </div>
              {menuLinks.map((page, index) => (
                <Link
                  key={index}
                  to={page.link}
                  onClick={() => setSelectedMenu(page.menuItem)}
                  style={{
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: "37px",
                    color: "rgba(255, 255, 255, 0.6)",
                    fontFamily: "Sofia Pro, sans-serif",
                  }}
                >
                  {page.name}
                </Link>
              ))}
            </Flex>
          </FooterColumn>
        </TopSection>

        {/* Divider */}
        <div
          style={{
            width: "100%",
            height: 1,
            background: "#171F41",
          }}
        />

        {/* Bottom Section */}
        <Flex
          style={{ width: "100%" }}
          gap={60}
          wrap="wrap"
          justify="center"
        >
          <div
            style={{
              fontSize: 16,
              lineHeight: "32px",
              textAlign: "center",
              fontFamily: "Sofia Pro, sans-serif",
            }}
          >
            Copyright © 2025 Všechny práva vyhrazena
          </div>
        </Flex>
      </FooterContent>
    </FooterWrapper>
  );
};

export default Footer;
